import React, { useRef, useState, useMemo } from "react";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  ThemeProvider,
  createTheme,
} from "@mui/material";
import { Download, Image as ImageIcon } from "@mui/icons-material";
import html2canvas from "html2canvas";
// @ts-ignore
import html2pdf from "html2pdf.js";
import { useMarkSheets } from "../../hooks/useMarkSheets";
import { useAuth } from "../../hooks/useAuth";
import { useSessionClass } from "../../contexts/SessionClassContext";

// Print-friendly theme
const theme = createTheme({
  typography: {
    fontFamily: "Arial, sans-serif",
  },
});

interface NoticeboardData {
  studentId?: number;
  studentRollno?: string;
  rollNumber?: string;
  studentName: string;
  fatherName?: string;
  caste?: string;
  gender?: string;
  totalMarks?: number;
  percentage?: number;
  grade?: string;
  className?: string;
  rank?: number;
  promote?: string;
  session?: string;
  [key: string]: any;
}

interface NoticeboardTemplateProps {
  noticeBoardData: NoticeboardData[];
  schoolName: string;
  district: string;
  block: string;
  diseCode: string;
  mpbseCode: string;
  className: string;
  examType: string;
  sessionYear: string;
  hidePrintButton?: boolean;
}

const NoticeboardTemplate: React.FC<NoticeboardTemplateProps> = ({
  noticeBoardData,
  schoolName,
  district,
  block,
  diseCode,
  mpbseCode,
  className,
  examType,
  sessionYear,
  hidePrintButton = false,
}) => {
  const templateRef = useRef<HTMLDivElement>(null);
  const [isDownloadingImage, setIsDownloadingImage] = useState(false);
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
  const { user } = useAuth();
  const isStudent = user?.roles?.includes('STUDENT');
  const { selectedClass, selectedSession } = useSessionClass();

  // Marksheets for current class/session — disable for students
  const { marksheetsQuery } = useMarkSheets(
    false,
    isStudent,
    selectedClass?.id,
    selectedSession?.id
  );
  const marksheets = marksheetsQuery.data || [];
  
  // Create a map of studentId + marksheetType + sessionYear -> result
  // Also create alternative maps for different key formats
  const marksheetResultMap = useMemo(() => {
    const map = new Map<string, string>();
    marksheets.forEach((marksheet: any) => {
      const studentId = marksheet.studentId || marksheet.student?.id;
      if (studentId && marksheet.marksheetType && marksheet.sessionYear && marksheet.result) {
        // Primary key: studentId_marksheetType_sessionYear
        const key1 = `${studentId}_${marksheet.marksheetType}_${marksheet.sessionYear}`;
        map.set(key1, marksheet.result);
        
        // Alternative key formats for better matching
        const key2 = `${studentId}_${marksheet.marksheetType.toUpperCase()}_${marksheet.sessionYear}`;
        map.set(key2, marksheet.result);
        
        const key3 = `${studentId}_${marksheet.marksheetType}_${marksheet.sessionYear.trim()}`;
        map.set(key3, marksheet.result);
      }
    });
    return map;
  }, [marksheets]);

  const downloadAsImage = async () => {
    if (!templateRef.current) return;
    setIsDownloadingImage(true);
    try {
      await new Promise((r) => setTimeout(r, 100));
      const el = templateRef.current!;
  
      const canvas = await html2canvas(
        el,
        {
          backgroundColor: "#ffffff", // ✅ correct property
          useCORS: true,
          allowTaint: true,
          logging: false,
          scale: 2,                   // ✅ runtime option (cast to any)
          windowWidth: el.clientWidth // ✅ runtime option (cast to any)
        } as any                      // ✅ cast to allow extra fields
      );
  
      const a = document.createElement("a");
      a.download = `notice-board-list-${className}-${examType}-${new Date()
        .toISOString()
        .split("T")[0]}.png`;
      a.href = canvas.toDataURL("image/png", 1.0);
      a.click();
    } catch (error) {
      alert(
        `Failed to download image: ${
          error instanceof Error ? error.message : "Unknown error"
        }`
      );
    } finally {
      setIsDownloadingImage(false);
    }
  };
  

  const downloadAsPdfFromImage = async () => {
    if (!templateRef.current) return;
    setIsDownloadingPdf(true);
    try {
      await new Promise((r) => setTimeout(r, 80));
      const el = templateRef.current!;

      // Ensure no internal scroll area inflates capture height
      const tc = el.querySelector(".MuiTableContainer-root") as HTMLElement | null;
      if (tc) {
        tc.style.overflow = "visible";
        tc.style.width = "100%";
        tc.style.maxHeight = "none";
      }

      const opt = {
        margin: [0, 0, 0, 0],
        filename: `notice-board-list-${className}-${examType}-${new Date()
          .toISOString()
          .split("T")[0]}.pdf`,
        image: { type: "jpeg", quality: 1.0 },
        html2canvas: {
          scale: 2,
          useCORS: true,
          allowTaint: true,
          backgroundColor: "#ffffff",
          windowWidth: el.clientWidth, // key line
          logging: false,
        },
        jsPDF: {
          unit: "mm",
          format: "a4",
          orientation: "portrait",
          compress: true,
          putOnlyUsedFonts: true,
        },
        // CSS-driven page breaks; no global avoid-all
        pagebreak: { mode: ["css", "legacy"] },
      };

      await html2pdf().set(opt).from(el).save();
    } catch (error) {
      alert("Failed to download PDF.");
    } finally {
      setIsDownloadingPdf(false);
    }
  };

  // Build a unified list for rendering (so we can map once)
  const rows: NoticeboardData[] =
    noticeBoardData.length > 0
      ? noticeBoardData
      : Array.from({ length: 8 }, (_, index) => ({
          studentId: index,
          studentRollno: `10A-B${1001 + index}`,
          studentName: `Sample Student ${index + 1}`,
          totalMarks: 480 - index * 5,
          percentage: Number((80 - index * 0.5).toFixed(2)),
          grade: "A",
          className,
        }));

  return (
    <ThemeProvider theme={theme}>
      {/* Keep colors + pagination behavior in PDF */}
      <style>{`
        * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        thead { display: table-header-group; }
        tfoot { display: table-footer-group; }

        /* Never split a row: each row is wrapped in its own tbody.nb-rowblock */
        tbody.nb-rowblock { 
          break-inside: avoid !important;
          page-break-inside: avoid !important;
        }
        tr, td, th {
          break-inside: avoid !important;
          page-break-inside: avoid !important;
        }
        table {
          break-inside: auto;
          page-break-inside: auto;
          border-collapse: collapse;
        }
      `}</style>

      <Box
        sx={{
          p: {xs: 0.5, sm: 2},
          backgroundColor: "#f5f5f5",
          width: "100%",
          // Mobile: prevent page-level horizontal scroll; only the sheet wrapper scrolls.
          overflowX: { xs: "hidden", sm: "visible" },
        }}
      >
        <Box
          sx={{
            overflowX: { xs: "auto", sm: "visible" },
            WebkitOverflowScrolling: "touch",
            width: "100%",
            // Desktop: keep centered like before
            display: { xs: "block", sm: "flex" },
            justifyContent: { sm: "center" },
          }}
        >
          <Paper
            ref={templateRef}
            sx={{
              p: 2,
              // Padding + border count inside 210mm so inner content never spills past the edge.
              boxSizing: "border-box",
              width: "210mm",
              maxWidth: "210mm",
              margin: "0 auto",
              backgroundColor: "#ffffff",
              boxShadow: "none",
              overflow: { xs: "hidden", sm: "visible" },
              position: "relative",
            }}
          >
            <Box
              sx={{
                width: "100%",
                minWidth: 0,
                boxSizing: "border-box",
              }}
            >
          {/* School Header */}
          <Box
            sx={{
              textAlign: "center",
              mb: 2,
              p: 2,
              backgroundColor: "#e8f5e8",
              borderRadius: 1,
            }}
          >
            <Typography
              variant="h5"
              sx={{
                fontWeight: "bold",
                fontSize: "20px",
                mb: 1.5,
                color: "#2e7d32",
                textTransform: "uppercase",
              }}
            >
              {schoolName}
            </Typography>

            <Box sx={{ display: "flex", justifyContent: "start" }}>
              <Box sx={{ textAlign: "left", pl: 2, fontWeight: "bold", width: "50%" }}>
                <Typography variant="body2" sx={{ fontSize: "14px", mb: 0.5, fontWeight: 700 }}>
                  DISTRICT- {district.toUpperCase()}
                </Typography>
                <Typography variant="body2" sx={{ fontSize: "14px", mb: 0.5, fontWeight: 700 }}>
                  BLOCK- {block.toUpperCase()}
                </Typography>
              </Box>
              <Box sx={{ textAlign: "left", pl: 2, fontWeight: "bold", width: "50%" }}>
                <Typography variant="body2" sx={{ fontSize: "14px", mb: 0.5, fontWeight: 700 }}>
                  DISE CODE- {diseCode.toUpperCase()}
                </Typography>
                <Typography variant="body2" sx={{ fontSize: "14px", fontWeight: 700 }}>
                  MPBSE CODE- {mpbseCode.toUpperCase()}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Title */}
          <Box
            sx={{
              textAlign: "center",
              mb: 2,
              p: 1.5,
              backgroundColor: "#fff3e0",
              borderRadius: 1,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: "bold",
                fontSize: "18px",
                color: "#e65100",
                textTransform: "uppercase",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: 2,
              }}
            >
              Notice Board List{" "}
              <Typography sx={{ fontSize: "17px", color: "#525454", fontWeight: "bold" }}>
                Class:- {className}
              </Typography>
            </Typography>
          </Box>

          {/* Table */}
          <TableContainer className="no-break-before" sx={{ width: "100%", overflow: "visible" }}>
            <Table
              sx={{
                border: "1px solid #000",
                width: "100%",
                "& .MuiTableCell-root": {
                  border: "1px solid #000",
                  padding: "8px",
                  fontSize: "12px",
                  textAlign: "center",
                  fontWeight: 500,
                },
              }}
            >
              <TableHead>
                <TableRow sx={{ backgroundColor: "#d0eaf7" }}>
                  <TableCell sx={{ fontWeight: "bold", width: "6%", fontSize: "13px" }}>
                    S.N.
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold", width: "14%", fontSize: "13px" }}>
                    ROLL NUMBER
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold", width: "22%", fontSize: "13px" }}>
                    STUDENT'S NAME
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold", width: "12%", fontSize: "13px" }}>
                    OBTAINED MARKS
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold", width: "10%", fontSize: "13px" }}>
                    PERCENTAGE
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold", width: "8%", fontSize: "13px" }}>
                    RESULT
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold", width: "10%", fontSize: "13px" }}>
                    DIVISION
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold", width: "18%", fontSize: "13px" }}>
                    CLASS / SECTION
                  </TableCell>
                </TableRow>
              </TableHead>

              {/* One tbody per row (prevents splitting a row across pages) */}
              {rows.map((student, index) => (
                <TableBody key={student.studentId || student.studentRollno || index} className="nb-rowblock">
                  <TableRow sx={{ backgroundColor: "#fffef7" }}>
                    <TableCell sx={{ fontWeight: 600 }}>{index + 1}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {student.studentRollno || student.rollNumber || ""}
                    </TableCell>
                    <TableCell sx={{ textAlign: "left", fontWeight: 600 }}>
                      {student.studentName}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {student.totalMarks ?? ""}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {student.percentage != null ? `${student.percentage}%` : ""}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {(() => {
                        // Get studentId from multiple possible sources
                        const studentId = student.studentId || (student as any).id || (student as any).student?.id;
                        let marksheetResult: string | undefined = undefined;
                        
                        // PRIORITY 1: Fetch directly from marksheets array (most reliable)
                        // This ensures we get the actual marksheet result from backend, not calculated values
                        if (studentId && examType && sessionYear && marksheets.length > 0) {
                          const matchingMarksheet = marksheets.find((m: any) => {
                            const mStudentId = m.studentId || m.student?.id;
                            const mType = m.marksheetType || "";
                            const mSession = m.sessionYear || "";
                            
                            return mStudentId && 
                                   Number(mStudentId) === Number(studentId) &&
                                   mType.toUpperCase() === examType.toUpperCase() &&
                                   mSession.trim() === sessionYear.trim() &&
                                   m.result;
                          });
                          
                          if (matchingMarksheet && matchingMarksheet.result) {
                            marksheetResult = matchingMarksheet.result;
                          }
                        }
                        
                        // PRIORITY 2: Try marksheet map lookup
                        if (!marksheetResult && studentId && examType && sessionYear) {
                          const key1 = `${studentId}_${examType}_${sessionYear}`;
                          const key2 = `${studentId}_${examType.toUpperCase()}_${sessionYear}`;
                          const key3 = `${studentId}_${examType}_${sessionYear.trim()}`;
                          
                          marksheetResult = marksheetResultMap.get(key1) || 
                                           marksheetResultMap.get(key2) || 
                                           marksheetResultMap.get(key3) || 
                                           undefined;
                        }
                        
                        // PRIORITY 3: Check nested marksheet object
                        if (!marksheetResult) {
                          marksheetResult = (student as any).marksheet?.result;
                        }
                        
                        // PRIORITY 4: Check student.result only if it's explicitly from marksheet (not calculated)
                        // Skip student.result if it might be calculated (divisionResult exists means it was calculated)
                        if (!marksheetResult && !student.divisionResult) {
                          marksheetResult = student.result || (student as any).marksheetResult;
                        }
                        
                        // If marksheet result exists and is valid, use it directly - NO FALLBACK TO CALCULATIONS
                        if (marksheetResult && typeof marksheetResult === "string") {
                          const resultUpper = marksheetResult.toUpperCase().trim();
                          if (resultUpper === "PASS" || resultUpper === "FAIL") {
                            return resultUpper;
                          }
                        }
                        
                        // If no marksheet result found, return empty string instead of calculating
                        // This ensures we only show results from marksheet, not frontend calculations
                        return "";
                      })()}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {(() => {
                        // Get studentId from multiple possible sources
                        const studentId = student.studentId || (student as any).id || (student as any).student?.id;
                        let marksheetResult: string | undefined = undefined;
                        
                        // PRIORITY 1: Fetch directly from marksheets array (most reliable)
                        if (studentId && examType && sessionYear && marksheets.length > 0) {
                          const matchingMarksheet = marksheets.find((m: any) => {
                            const mStudentId = m.studentId || m.student?.id;
                            const mType = m.marksheetType || "";
                            const mSession = m.sessionYear || "";
                            
                            return mStudentId && 
                                   Number(mStudentId) === Number(studentId) &&
                                   mType.toUpperCase() === examType.toUpperCase() &&
                                   mSession.trim() === sessionYear.trim() &&
                                   m.result;
                          });
                          
                          if (matchingMarksheet && matchingMarksheet.result) {
                            marksheetResult = matchingMarksheet.result;
                          }
                        }
                        
                        // PRIORITY 2: Try marksheet map lookup
                        if (!marksheetResult && studentId && examType && sessionYear) {
                          const key1 = `${studentId}_${examType}_${sessionYear}`;
                          const key2 = `${studentId}_${examType.toUpperCase()}_${sessionYear}`;
                          const key3 = `${studentId}_${examType}_${sessionYear.trim()}`;
                          
                          marksheetResult = marksheetResultMap.get(key1) || 
                                           marksheetResultMap.get(key2) || 
                                           marksheetResultMap.get(key3) || 
                                           undefined;
                        }
                        
                        // PRIORITY 3: Check nested marksheet object
                        if (!marksheetResult) {
                          marksheetResult = (student as any).marksheet?.result;
                        }
                        
                        // PRIORITY 4: Check student.result only if it's explicitly from marksheet (not calculated)
                        if (!marksheetResult && !student.divisionResult) {
                          marksheetResult = student.result || (student as any).marksheetResult;
                        }
                        
                        // First check if student passed or failed from marksheet
                        if (marksheetResult && typeof marksheetResult === "string") {
                          const resultUpper = marksheetResult.toUpperCase().trim();
                          if (resultUpper === "FAIL") {
                            // If student failed, division is always FAIL
                            return "FAIL";
                          } else if (resultUpper === "PASS") {
                            // If student passed, calculate division based on percentage
                            if (typeof student.percentage === "number") {
                              if (student.percentage >= 60) {
                                return "FIRST DIVISION";
                              } else if (student.percentage >= 50) {
                                return "SECOND DIVISION";
                              } else if (student.percentage >= 33) {
                                return "THIRD DIVISION";
                              } else {
                                return "FAIL";
                              }
                            }
                            // If passed but no percentage, return empty or default
                            return "";
                          }
                        }
                        
                        // Fallback: Use student.division if available, otherwise calculate based on percentage
                        if (student.division) {
                          return student.division;
                        }
                        
                        // Last resort: Calculate based on percentage (only if no marksheet result found)
                        if (typeof student.percentage === "number") {
                          if (student.percentage < 33) {
                            return "FAIL";
                          } else if (student.percentage >= 60) {
                            return "FIRST DIVISION";
                          } else if (student.percentage >= 50) {
                            return "SECOND DIVISION";
                          } else {
                            return "THIRD DIVISION";
                          }
                        }
                        
                        return "";
                      })()}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {student.className || ""}
                    </TableCell>
                  </TableRow>
                </TableBody>
              ))}
            </Table>
          </TableContainer>

          {/* Footer */}
          <Box sx={{ mt: 2, textAlign: "center", fontSize: "11px", color: "#666" }}>
            <Typography variant="body2">
              Exam Type: {examType} | Session: {sessionYear}
            </Typography>
          </Box>
            </Box>
        </Paper>
        </Box>

        {!hidePrintButton && (
          <Box
            sx={{
              display: "flex",
              gap: 2,
              mb: 2,
              justifyContent: "center",
              flexWrap: "wrap",
              width: "100%",
              overflowX: { xs: "hidden", sm: "visible" },
            }}
          >
            <Button
              variant="contained"
              startIcon={<ImageIcon />}
              onClick={downloadAsImage}
              disabled={isDownloadingImage}
              sx={{ bgcolor: "#1976d2", minWidth: { xs: "100%", sm: "64px" }, maxWidth: "100%" }}
            >
              {isDownloadingImage ? "Downloading..." : "Download as Image"}
            </Button>
            <Button
              variant="contained"
              startIcon={<Download />}
              onClick={downloadAsPdfFromImage}
              disabled={isDownloadingPdf}
              sx={{ bgcolor: "#d32f2f", minWidth: { xs: "100%", sm: "64px" }, maxWidth: "100%" }}
            >
              {isDownloadingPdf ? "Downloading..." : "Download as PDF"}
            </Button>
          </Box>
        )}
      </Box>
    </ThemeProvider>
  );
};

export default NoticeboardTemplate;
